/**
 * WarehouseDrilldown — collapsible per-warehouse breakdown.
 *
 * §5.BU §I.3 P3 Commit B (2026-06-19). Locked in
 *   docs/audits/2026-06-19_5bu_i3_p2_design.md §E
 *
 * Per-warehouse row shape from the endpoint response:
 *   { warehouse_code, warehouse_label, warehouse_label_display,
 *     unique_skus, on_hand_dollar, osmi_dollar, over_max_dollar,
 *     open_po_dollar }
 *
 * Sort: ORDER BY on_hand_dollar DESC (backend already sorts; frontend
 * renders in received order).
 *
 * Per N4 lock (P2 §E.5): render drilldown unconditionally even for
 * single-warehouse suppliers (54% of XMEK suppliers per P1 §5.4) —
 * consistency over elegance for v1.
 *
 * Negative inventory rendering (P2 §E.4): negative on_hand_dollar
 * cells rendered in muted red text with sup-script footnote anchor.
 */
import React, { useState } from 'react';

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): read the `.sd`
// Linear token layer so the drill-down flips with the shell in the future dark
// lane.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

function formatCurrency(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return 'Not available';
  return `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatInt(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return 'Not available';
  return Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function MoneyCell({ value, mutedZero = true }) {
  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--text-3)' }}>Not available</span>;
  }
  const n = Number(value);
  if (n < 0) {
    return (
      <span
        data-testid="negative-money-cell"
        style={{ color: 'var(--crit)', fontFamily: 'monospace' }}
        title="Negative net allocation"
      >
        {formatCurrency(value)}
      </span>
    );
  }
  if (mutedZero && n === 0) {
    return (
      <span style={{ color: 'var(--text-3)', fontFamily: 'monospace' }}>
        $0
      </span>
    );
  }
  return (
    <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>
      {formatCurrency(value)}
    </span>
  );
}

function WarehouseDrilldown({ warehouses, collapsedByDefault = true }) {
  const [open, setOpen] = useState(!collapsedByDefault);
  const list = warehouses || [];

  // Don't render anything when there are no warehouse rows — the parent
  // tab already shows an empty-state when has_data is false. This case
  // would mean the supplier has inventory but somehow it's not bucketed
  // into any warehouse (shouldn't happen in practice).
  if (list.length === 0) return null;

  return (
    <div style={{ ...LINEAR_PANEL, padding: 0, marginBottom: '24px', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="warehouse-drilldown-toggle"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              color: 'var(--text-3)',
              fontSize: 'var(--font-size-xs)',
              width: '12px',
              flexShrink: 0,
            }}
          >
            {open ? '▾' : '▸'}
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text)',
            }}
          >
            Warehouse breakdown
          </span>
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-3)' }}>
            {list.length} warehouse{list.length === 1 ? '' : 's'}
          </span>
        </div>
      </button>

      {open && (
        <div
          data-testid="warehouse-drilldown-panel"
          style={{ overflowX: 'auto', borderTop: '1px solid var(--hair)' }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[
                  'Warehouse',
                  'Unique SKUs',
                  'On-Hand $',
                  'OSMI $',
                  'Over-Max $',
                  'Open PO $',
                ].map((h, idx) => (
                  <th
                    key={h}
                    style={{
                      textAlign: idx === 0 ? 'left' : 'right',
                      padding: '10px 12px',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text-3)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      borderBottom: '1px solid var(--border)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr
                  key={row.warehouse_code}
                  data-testid="warehouse-drilldown-row"
                  data-warehouse-code={row.warehouse_code}
                  style={{ borderBottom: '1px solid var(--hair)' }}
                >
                  <td
                    style={{
                      padding: '10px 12px',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--text)',
                      fontWeight: 'var(--font-weight-medium)',
                    }}
                  >
                    {row.warehouse_label_display || row.warehouse_label
                      || `Warehouse ${row.warehouse_code}`}
                  </td>
                  <td
                    style={{
                      padding: '10px 12px',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--text)',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                    }}
                  >
                    {formatInt(row.unique_skus)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <MoneyCell value={row.on_hand_dollar} mutedZero={false} />
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <MoneyCell value={row.osmi_dollar} />
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <MoneyCell value={row.over_max_dollar} />
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <MoneyCell value={row.open_po_dollar} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default WarehouseDrilldown;
